import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { messagingService } from '../../services/messagingService';
import { authService } from '../../services/apiService';

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  padding: 2.5rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  height: calc(100vh - 100px);
  border-radius: 0;
  min-height: calc(100vh - 100px);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    height: auto;
    padding: 1.5rem;
  }
`;

const ConversationsList = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: none;
  border-top: 4px solid #667eea;
`;

const Header = styled.div`
  padding: 1.5rem;
  border-bottom: 2px solid #667eea;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4f8 100%);

  h2 {
    margin: 0;
    color: #2c3e50;
    font-size: 1.2rem;
    font-weight: 800;
  }
`;

const ConversationsContainer = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const ConversationItem = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #ecf0f1;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f8fbfe;
  }

  ${props => props.selected && `
    background-color: #e8f5f4;
    border-left: 4px solid #2ecc71;
    padding-left: calc(1.5rem - 4px);
  `}
`;

const ConversationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 0.5rem;
`;

const Name = styled.h3`
  margin: 0;
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 700;
`;

const Phone = styled.p`
  margin: 0.25rem 0 0 0;
  color: #666;
  font-size: 0.85rem;
`;

const CropInfo = styled.p`
  margin: 0.5rem 0 0 0;
  color: #2ecc71;
  font-size: 0.9rem;
  font-weight: 600;
`;

const LastMessage = styled.p`
  margin: 0.5rem 0 0 0;
  color: #666;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-size: 0.75rem;
`;

const Timestamp = styled.span`
  color: #999;
`;

const UnreadBadge = styled.span`
  background: #2ecc71;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
`;

const ChatArea = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: none;
  border-top: 4px solid #764ba2;
`;

const ChatHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 2px solid #764ba2;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4f8 100%);

  h2 {
    margin: 0 0 0.2rem 0;
    color: #2c3e50;
    font-size: 1.1rem;
    font-weight: 800;
  }

  p {
    margin: 0;
    color: #7f8c8d;
    font-size: 0.8rem;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MessageBubble = styled.div`
  display: flex;
  justify-content: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
  margin-bottom: 0.5rem;
`;

const Message = styled.div`
  max-width: 70%;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  word-wrap: break-word;
  background: ${props => props.isOwn ? 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)' : '#ecf0f1'};
  color: ${props => props.isOwn ? 'white' : '#2c3e50'};
  font-size: 0.95rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
`;

const MessageTime = styled.div`
  font-size: 0.75rem;
  color: #999;
  margin-top: 0.25rem;
  text-align: ${props => props.isOwn ? 'right' : 'left'};
  padding: 0 1rem;
`;

const InputArea = styled.div`
  padding: 1.2rem;
  border-top: 2px solid #ecf0f1;
  background: #f8f9fb;
`;

const InputForm = styled.form`
  display: flex;
  gap: 0.5rem;
`;

const MessageInput = styled.textarea`
  flex: 1;
  padding: 0.8rem;
  border: 2px solid #ecf0f1;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.9rem;
  resize: none;
  min-height: 38px;
  max-height: 80px;
  transition: all 0.3s ease;
  background: #f8f9fb;

  &:focus {
    outline: none;
    border-color: #2ecc71;
    background: white;
    box-shadow: 0 0 0 3px rgba(46, 204, 113, 0.1);
  }

  &::placeholder {
    color: #b0b8c1;
  }
`;

const SendButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.4rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
  height: fit-content;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #999;
  text-align: center;

  div {
    h3 {
      color: #666;
      margin: 0 0 0.5rem 0;
    }

    p {
      margin: 0;
      font-size: 0.9rem;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #666;
`;

function formatTime(date) {
  const now = new Date();
  const messageDate = new Date(date);
  const diffInHours = (now - messageDate) / (1000 * 60 * 60);

  if (diffInHours < 1) {
    const diffInMinutes = Math.round((now - messageDate) / (1000 * 60));
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${Math.round(diffInHours)}h ago`;
  } else {
    return messageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

function formatMessageTime(date) {
  return new Date(date).toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
}

export function Messaging() {
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messageContent, setMessageContent] = useState('');
  const [selectedPeerId, setSelectedPeerId] = useState(null);
  const [selectedPeerInfo, setSelectedPeerInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [role, setRole] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const userId = parseInt(localStorage.getItem('userId')) || 0;
  const buyerId = parseInt(localStorage.getItem('buyerId')) || 0;
  const sellerId = parseInt(localStorage.getItem('sellerId')) || 0;
  const [searchParams, setSearchParams] = useSearchParams();
  const querysellerId = searchParams.get('sellerId');
  const querySellerName = searchParams.get('sellerName');
  const queryCropType = searchParams.get('cropType');

  console.log('🔍 [Messaging] Render - role:', role, 'isLoading:', isLoading, 'error:', error, 'userId:', userId, 'buyerId:', buyerId);

  // Initialize role on mount
  useEffect(() => {
    console.log('📍 [Messaging] Mount effect - reading role from localStorage');
    const userRole = localStorage.getItem('userRole');
    console.log('✅ [Messaging] Role from storage:', userRole);
    setRole(userRole);
  }, []);

  // Fetch conversations when role is set
  useEffect(() => {
    if (!role) {
      console.log('⏳ [Messaging] No role yet, skipping fetch');
      return;
    }

    const storedBuyerId = parseInt(localStorage.getItem('buyerId')) || 0;
    const storedSellerId = parseInt(localStorage.getItem('sellerId')) || 0;
    
    console.log('👤 [Messaging] Role set to:', role, 'buyerId:', storedBuyerId, 'sellerId:', storedSellerId);

    // If buyer has query params, skip fetch
    if (role === 'Buyer' && querysellerId) {
      console.log('📍 [Messaging] Query params detected, skipping conversation list fetch');
      setIsLoading(false);
      return;
    }

    // Fetch conversations for this role
    if (role === 'Buyer' && storedBuyerId > 0) {
      console.log('👤 [Messaging] Fetching buyer conversations for buyerId:', storedBuyerId);
      messagingService.getBuyerSellers(storedBuyerId)
        .then(data => {
          console.log('✅ [Messaging] Got buyer conversations:', data);
          // Ensure we're working with an array
          const conversations = Array.isArray(data) ? data : (data?.$values || []);
          setConversations(conversations);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('❌ [Messaging] Error fetching buyer conversations:', err);
          setError(err.message || 'Failed to load conversations');
          setIsLoading(false);
        });
    } else if (role === 'Seller' && storedSellerId > 0) {
      console.log('👤 [Messaging] Fetching seller conversations for sellerId:', storedSellerId);
      messagingService.getSellerMessages(storedSellerId)
        .then(data => {
          console.log('✅ [Messaging] Got seller conversations:', data);
          // Ensure we're working with an array
          const conversations = Array.isArray(data) ? data : (data?.$values || []);
          setConversations(conversations);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('❌ [Messaging] Error fetching seller conversations:', err);
          setError(err.message || 'Failed to load conversations');
          setIsLoading(false);
        });
    } else if ((role === 'Buyer' && storedBuyerId === 0) || (role === 'Seller' && storedSellerId === 0)) {
      // No ID stored - try to fetch profile
      console.log('⚠️ [Messaging] No ID in storage for role:', role, '- fetching profile for userId:', userId);
      if (userId > 0) {
        authService.getUserProfile(userId)
          .then(profile => {
            console.log('✅ [Messaging] Profile fetched:', profile);
            let id = 0;
            if (role === 'Buyer' && profile?.buyerProfile?.buyerId) {
              id = profile.buyerProfile.buyerId;
              localStorage.setItem('buyerId', id);
              return messagingService.getBuyerSellers(id);
            } else if (role === 'Seller' && profile?.sellerProfile?.sellerId) {
              id = profile.sellerProfile.sellerId;
              localStorage.setItem('sellerId', id);
              return messagingService.getSellerMessages(id);
            } else {
              throw new Error(`Profile missing ${role === 'Buyer' ? 'buyerId' : 'sellerId'}`);
            }
          })
          .then(data => {
            console.log('✅ [Messaging] Got conversations after profile fetch:', data);
            // Ensure we're working with an array
            const conversations = Array.isArray(data) ? data : (data?.$values || []);
            setConversations(conversations);
            setIsLoading(false);
          })
          .catch(err => {
            console.error('❌ [Messaging] Error in profile fetch flow:', err);
            setError(err.message || 'Failed to load profile or conversations');
            setIsLoading(false);
          });
      } else {
        console.error('❌ [Messaging] No userId found');
        setError('No user ID found in storage');
        setIsLoading(false);
      }
    } else {
      console.warn('⚠️ [Messaging] Unknown role or no ID:', role);
      setIsLoading(false);
    }
  }, [role, querysellerId, userId]);

  // If buyer has query params, open that seller's chat directly
  useEffect(() => {
    if (querysellerId && role === 'Buyer') {
      console.log('📍 [Messaging] Opening direct seller chat from query params:', querysellerId);
      const sellerId = parseInt(querysellerId);
      setSelectedPeerId(sellerId);
      setSelectedPeerInfo({
        sellerId: sellerId,
        sellerName: decodeURIComponent(querySellerName || 'Seller'),
        cropType: decodeURIComponent(queryCropType || 'Crop'),
        basePrice: 0
      });
      console.log('✅ [Messaging] Selected peer set to:', sellerId);
      // Note: fetchBuyerSellerChat will be called by the third useEffect when selectedPeerId changes
    }
  }, [querysellerId, role, querySellerName, queryCropType]);

  // Fetch messages when peer is selected
  useEffect(() => {
    if (selectedPeerId) {
      console.log('💬 [Messaging] Fetching chat for selectedPeerId:', selectedPeerId, 'role:', role, 'buyerId:', buyerId, 'sellerId:', sellerId);
      
      if (role === 'Buyer') {
        if (buyerId > 0) {
          fetchBuyerSellerChat(selectedPeerId);
          const interval = setInterval(() => fetchBuyerSellerChat(selectedPeerId), 5000);
          return () => clearInterval(interval);
        } else {
          console.warn('⚠️ [Messaging] Buyer role but buyerId is 0 or invalid. Trying to fetch profile...');
          // Try to fetch profile again to get buyerId
          const userId = parseInt(localStorage.getItem('userId')) || 0;
          if (userId > 0) {
            import('../../services/apiService').then(({ authService }) => {
              authService.getUserProfile(userId)
                .then((profile) => {
                  if (profile.buyerProfile?.buyerId) {
                    localStorage.setItem('buyerId', profile.buyerProfile.buyerId);
                    console.log('✅ [Messaging] Re-fetched profile, got buyerId:', profile.buyerProfile.buyerId);
                    // Now fetch chat with the updated buyerId
                    setTimeout(() => fetchBuyerSellerChat(selectedPeerId), 100);
                  }
                })
                .catch((err) => {
                  console.error('❌ [Messaging] Failed to re-fetch profile:', err);
                });
            });
          }
        }
      } else if (role === 'Seller') {
        if (sellerId > 0) {
          fetchSellerBuyerChat(selectedPeerId);
          const interval = setInterval(() => fetchSellerBuyerChat(selectedPeerId), 5000);
          return () => clearInterval(interval);
        } else {
          console.warn('⚠️ [Messaging] Seller role but sellerId is 0 or invalid');
        }
      }
    }
  }, [selectedPeerId, role, buyerId, sellerId]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchBuyerConversations = async () => {
    try {
      const data = await messagingService.getBuyerSellers(buyerId);
      setConversations(data || []);
    } catch (error) {
      console.error('Error fetching buyer conversations:', error);
      setConversations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSellerConversations = async () => {
    try {
      const data = await messagingService.getSellerMessages(sellerId);
      setConversations(data || []);
    } catch (error) {
      console.error('Error fetching seller conversations:', error);
      setConversations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBuyerSellerChat = async (peerId) => {
    try {
      const data = await messagingService.getBuyerSellerChat(buyerId, peerId);
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching buyer-seller chat:', error);
      setMessages([]);
    }
  };

  const fetchSellerBuyerChat = async (peerId) => {
    try {
      const data = await messagingService.getSellerBuyerChat(peerId, sellerId);
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching seller-buyer chat:', error);
      setMessages([]);
    }
  };

  const handleSelectPeer = (peer) => {
    if (role === 'Buyer') {
      setSelectedPeerId(peer.sellerId);
      setSelectedPeerInfo(peer);
      setSearchParams({ sellerId: peer.sellerId });
    } else {
      setSelectedPeerId(peer.buyerId);
      setSelectedPeerInfo(peer);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageContent.trim() || !selectedPeerId) {
      return;
    }

    setIsSending(true);
    try {
      // Send userId (from Users table) not buyerId/sellerId (from Buyers/Sellers tables)
      // This satisfies the FK constraints on Messages.SenderId → Users.UserId
      await messagingService.sendMessage(userId, selectedPeerId, null, messageContent);
      setMessageContent('');
      
      setTimeout(async () => {
        try {
          if (role === 'Buyer') {
            await fetchBuyerSellerChat(selectedPeerId);
          } else {
            await fetchSellerBuyerChat(selectedPeerId);
          }
        } catch (err) {
          console.error('Error refreshing messages:', err);
        }
      }, 500);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const formatTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  const formatMessageTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'N/A';
    }
  };

  if (!role) {
    return (
      <Container>
        <EmptyState>
          <div>
            <h3>⏳ Loading Messaging...</h3>
            <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
              UserID: {userId || 'none'} | BuyerID: {buyerId || 'none'} | SellerID: {sellerId || 'none'}
            </p>
            <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
              (If this takes too long, try refreshing the page)
            </p>
          </div>
        </EmptyState>
      </Container>
    );
  }

  try {
    return (
      <Container>
          <ConversationsList>
            <Header>
              <h2>💬 {role === 'Buyer' ? 'Seller Chats' : 'Buyer Messages'}</h2>
            </Header>
            <ConversationsContainer>
            {error ? (
              <EmptyState>
                <div>
                  <h3>⚠️ Error Loading Conversations</h3>
                  <p style={{ color: '#e74c3c', fontSize: '0.9rem' }}>{error}</p>
                  <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '1rem' }}>
                    Role: {role || 'none'} | UserID: {userId || 'none'} | BuyerID: {buyerId || 'none'} | SellerID: {sellerId || 'none'}
                  </p>
                  <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#2ecc71', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    Reload Page
                  </button>
                </div>
              </EmptyState>
            ) : isLoading ? (
            <LoadingContainer>
              <div>
                <h3>Loading conversations...</h3>
                <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
                  Role: {role || 'none'} | BuyerID: {buyerId || 'none'} | SellerID: {sellerId || 'none'}
                </p>
              </div>
            </LoadingContainer>
          ) : !role ? (
            <EmptyState>
              <h3>🔐 Login Required</h3>
              <p>Please log in to access messages</p>
            </EmptyState>
          ) : conversations.length === 0 ? (
            <EmptyState>
              <h3>No conversations yet</h3>
              <p>{role === 'Buyer' ? 'Message a seller to start' : 'Wait for buyers to contact you'}</p>
            </EmptyState>
          ) : (
            conversations.map((conv) => {
              const peerId = role === 'Buyer' ? conv.sellerId : conv.buyerId;
              return (
                <ConversationItem
                  key={peerId}
                  selected={selectedPeerId === peerId}
                  onClick={() => handleSelectPeer(conv)}
                >
                  <ConversationHeader>
                    <div>
                      <Name>{role === 'Buyer' ? conv.sellerName : conv.buyerName}</Name>
                      <Phone>{role === 'Buyer' ? conv.sellerPhone : conv.buyerPhone}</Phone>
                    </div>
                    {conv.unreadCount > 0 && <UnreadBadge>{conv.unreadCount}</UnreadBadge>}
                  </ConversationHeader>
                  <LastMessage>{conv.lastMessage || 'No messages yet'}</LastMessage>
                  <Meta>
                    <Timestamp>{formatTime(conv.lastMessageTime)}</Timestamp>
                  </Meta>
                </ConversationItem>
              );
            })
          )}
        </ConversationsContainer>
      </ConversationsList>

      <ChatArea>
        {selectedPeerId && selectedPeerInfo ? (
          <>
            <ChatHeader>
              <h2>{role === 'Buyer' ? selectedPeerInfo.sellerName : selectedPeerInfo.buyerName}</h2>
            </ChatHeader>

            <MessagesContainer>
              {messages.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
                  No messages yet. Start the conversation!
                </div>
              ) : (
                messages.map((message) => (
                  <MessageBubble 
                    key={message.messageId}
                    isOwn={role === 'Buyer' ? message.senderRole === 'Buyer' : message.senderId === sellerId}
                  >
                    <Message isOwn={role === 'Buyer' ? message.senderRole === 'Buyer' : message.senderId === sellerId}>
                      {message.messageContent}
                    </Message>
                    <MessageTime isOwn={role === 'Buyer' ? message.senderRole === 'Buyer' : message.senderId === sellerId}>
                      {formatMessageTime(message.createdAt)}
                    </MessageTime>
                  </MessageBubble>
                ))
              )}
              <div ref={messagesEndRef} />
            </MessagesContainer>

            <InputArea>
              <InputForm onSubmit={handleSendMessage}>
                <MessageInput
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message... (shift+enter for new line)"
                  disabled={isSending}
                />
                <SendButton type="submit" disabled={isSending || !messageContent.trim()}>
                  {isSending ? 'Sending...' : 'Send'}
                </SendButton>
              </InputForm>
            </InputArea>
          </>
        ) : (
          <EmptyState>
            <h3>Select a conversation</h3>
            <p>Choose from the list to start messaging</p>
          </EmptyState>
        )}
      </ChatArea>
    </Container>
    );
  } catch (err) {
    console.error('❌ [Messaging] Render error:', err);
    return (
      <Container>
        <EmptyState>
          <div>
            <h3>❌ Rendering Error</h3>
            <p style={{ color: '#e74c3c', fontSize: '0.9rem' }}>{err?.message || 'Unknown error'}</p>
            <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#2ecc71', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Reload Page
            </button>
          </div>
        </EmptyState>
      </Container>
    );
  }
}


